import React from 'react'
import { isServicesEnabled, isServiceFeatureEnabled, getServicesAlternativeMessage } from '../config/services';
import { isBookingEnabled, shouldShowBookingIn, getBookingButtonText } from '../config/booking';

const Services = () => {
  const services = [
    {
      title: "Panchakarma Therapy",
      description: "Complete detoxification and rejuvenation treatment using five traditional procedures.",
      icon: "🌿",
      features: ["Vamana (Emesis)", "Virechana (Purgation)", "Basti (Enema)", "Nasya (Nasal)", "Raktamokshana"],
      duration: "7-21 days",
      price: "₹15,000 - ₹45,000"
    },
    {
      title: "Abhyanga Massage",
      description: "Full-body therapeutic oil massage to improve circulation and reduce stress.",
      icon: "💆‍♀️",
      features: ["Herbal oil massage", "Stress relief", "Improved circulation", "Muscle relaxation", "Skin nourishment"],
      duration: "60-90 minutes",
      price: "₹2,500 - ₹4,000"
    },
    {
      title: "Shirodhara",
      description: "Continuous pouring of medicated oil on forehead for mental peace and clarity.",
      icon: "🧘‍♀️",
      features: ["Stress reduction", "Mental clarity", "Better sleep", "Headache relief", "Hair nourishment"],
      duration: "45-60 minutes",
      price: "₹3,000 - ₹5,000"
    },
    {
      title: "Herbal Steam Bath",
      description: "Medicated steam therapy to open pores and eliminate toxins from the body.",
      icon: "♨️",
      features: ["Toxin elimination", "Weight management", "Skin purification", "Joint relief", "Relaxation"],
      duration: "30-45 minutes",
      price: "₹1,500 - ₹2,500"
    },
    {
      title: "Ayurvedic Consultation",
      description: "Comprehensive health assessment and personalized treatment planning.",
      icon: "👨‍⚕️",
      features: ["Pulse diagnosis", "Body constitution analysis", "Diet planning", "Lifestyle guidance", "Medicine prescription"],
      duration: "45-60 minutes",
      price: "₹1,000 - ₹2,000"
    },
    {
      title: "Yoga & Meditation",
      description: "Guided yoga sessions and meditation practices for holistic wellness.",
      icon: "🧘",
      features: ["Asana practice", "Pranayama", "Meditation", "Stress management", "Flexibility improvement"],
      duration: "60-90 minutes",
      price: "₹500 - ₹1,500"
    }
  ]

  // Check if services should be displayed
  if (!isServicesEnabled()) {
    const alternativeConfig = getServicesAlternativeMessage();
    
    if (!alternativeConfig.showMessage) {
      return null; // Don't render anything if services are disabled and no message should show
    }
    
    return (
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-green-600">Services</span>
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-brown-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔄</span>
              </div>
              <p className="text-xl text-gray-600 mb-4">{alternativeConfig.message}</p>
              {alternativeConfig.redirectToContact && (
                <button className="bg-gradient-green text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors duration-200">
                  Contact Us for Details
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-green-600">Ayurvedic</span> <span className="text-brown-600">Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover our comprehensive range of authentic Ayurvedic treatments designed to restore balance, 
            promote healing, and enhance your overall well-being through natural methods.
          </p>
        </div>

        {/* Services Grid */}
        {isServiceFeatureEnabled('showServiceCards') ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
            <div key={index} className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:border-green-200 hover:shadow-xl transition-all duration-300 group">
              {/* Service Icon */}
              <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>

              {/* Service Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-300">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 mb-4 leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-brown-600 mb-2 uppercase tracking-wide">Includes:</h4>
                <ul className="space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-center">
                      <span className="text-green-500 mr-2 text-xs">●</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Duration and Price */}
              <div className="border-t border-gray-100 pt-4 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Duration:</span>
                  <span className="text-sm font-medium text-gray-700">{service.duration}</span>
                </div>
                {isServiceFeatureEnabled('showPricing') && (
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-gray-500">Price:</span>
                    <span className="text-lg font-bold text-green-600">{service.price}</span>
                  </div>
                )}

                {/* Book Now Button */}
                {shouldShowBookingIn('Services') && (
                  <button className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105">
                    {getBookingButtonText('primary')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📋</span>
              </div>
              <p className="text-gray-600">Service details are currently being updated.</p>
            </div>
          </div>
        )}

        {/* Additional Info */}
        {isServiceFeatureEnabled('showAdditionalInfo') && (
          <div className="mt-16 bg-gradient-to-r from-green-50 to-brown-50 rounded-2xl p-8">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose Our <span className="text-green-600">Treatments</span>?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🌱</span>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">100% Natural</h4>
                  <p className="text-gray-600">All treatments use pure, organic herbs and natural ingredients</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-brown-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">👨‍⚕️</span>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Expert Care</h4>
                  <p className="text-gray-600">Administered by certified Ayurvedic practitioners</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🎯</span>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Personalized</h4>
                  <p className="text-gray-600">Customized treatment plans based on your unique needs</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

export default Services
